import { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, IconButton, Drawer, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import { Menu, X, LogIn, Phone, Mail } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

export function Navbar({ data }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();

  const primaryColor = data.primary_color || '#1e3a8a';
  const logo = data.logo || '/shreeji-logo-clean.png';
  const schoolName = data.school_name || 'Hera Public School';

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 40) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Home', href: '#hero' },
    { label: 'About', href: '#about' },
    { label: 'Academics', href: '#academics' },
    { label: 'Facilities', href: '#facilities' },
    { label: 'Events', href: '#events' },
    { label: 'Gallery', href: '#gallery' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <>
      {/* Top Contact Bar */}
      <Box
        sx={{
          backgroundColor: primaryColor,
          color: '#ffffff',
          py: 0.8,
          fontSize: '0.825rem',
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Phone size={14} />
              <span>{data.contact?.phone || '+91 98765 43210'}</span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Mail size={14} />
              <span>{data.contact?.email || 'info@herapublicschool.edu'}</span>
            </Box>
          </Box>
          <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 500 }}>
            {data.quick_info?.affiliation ? `Affiliated to ${data.quick_info.affiliation}` : 'Affiliated to CBSE'}
          </Typography>
        </Container>
      </Box>

      {/* Main Navbar */}
      <Box
        component="header"
        id="navbar"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.96)' : '#ffffff',
          backdropFilter: scrolled ? 'blur(12px)' : 'none',
          boxShadow: scrolled ? '0 10px 30px rgba(0, 0, 0, 0.08)' : '0 2px 10px rgba(0, 0, 0, 0.04)',
          transition: 'all 0.3s ease',
          py: scrolled ? 1.2 : 1.8,
        }}
      >
        <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Logo & Brand */}
          <Box
            component="a"
            href="#hero"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            {!logoError && logo ? (
              <Box
                component="img"
                src={logo}
                alt={schoolName}
                onError={() => setLogoError(true)}
                sx={{
                  height: { xs: 42, md: 52 },
                  maxWidth: 220,
                  objectFit: 'contain',
                }}
              />
            ) : (
              <Box>
                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{
                    color: primaryColor,
                    lineHeight: 1.1,
                    fontSize: { xs: '1.1rem', md: '1.35rem' },
                    letterSpacing: '-0.02em',
                  }}
                >
                  {schoolName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 500 }}>
                  {data.tagline || 'Empowering Young Minds'}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Desktop Nav Items */}
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 3 }}>
            {navItems.map((item) => (
              <Typography
                key={item.label}
                component="a"
                href={item.href}
                variant="body2"
                fontWeight={600}
                sx={{
                  color: '#334155',
                  textDecoration: 'none',
                  transition: 'all 0.2s',
                  '&:hover': {
                    color: primaryColor,
                  },
                }}
              >
                {item.label}
              </Typography>
            ))}
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>

            <IconButton
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ display: { xs: 'flex', lg: 'none' }, color: '#334155' }}
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </IconButton>
          </Box>
        </Container>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: { width: 280, p: 3 },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight={700} color={primaryColor}>
            Menu
          </Typography>
          <IconButton onClick={() => setMobileOpen(false)}>
            <X size={20} />
          </IconButton>
        </Box>
        <List>
          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                component="a"
                href={item.href}
                onClick={() => setMobileOpen(false)}
                sx={{ borderRadius: 1.5, mb: 0.5 }}
              >
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600 }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </>
  );
}
